import type { DefaultSubmissionStatusValue } from '../config/instance-settings-registry';
import type {
	OrderSubmissionCheckPreference,
	OrderSubmissionCheckType,
} from '../models/types/order-preferences.types';
import { ApprovalRequiredReason } from '../models/types/order-preferences.types';

/**
 * PD-14234 — role-based order-approval mapping.
 *
 * Approver roles are configured per submission check (and on the Default
 * Order Status setting for DEFAULT_SETTING holds), but orders persist
 * APPROVAL REASONS — and several checks collapse to the same reason (e.g.
 * daily/monthly/weight/controlled-substance checks all write
 * CONTROLLED_SUBSTANCE_LIMIT). This module maps a held order's reasons back
 * to the check rows that could have produced them and answers "do these
 * roles cover every reason on this order?".
 *
 * Semantics (agreed on the ticket):
 * - Users with the blanket orders:approve / orders:reject actions can
 *   always act — the mapping only ADDS scoped rights, it never restricts.
 * - A role covers a reason when it is listed on ANY check row that can
 *   produce that reason (union across the colliding checks — granularity
 *   below the reason level is already lost on the order itself).
 * - A user may approve/reject an order only when every reason on it is
 *   covered for the order's channel (crm vs ecommerce).
 */

export type OrderApprovalChannel = 'crm' | 'ecommerce';

/**
 * Which submission-check rows can produce each approval reason. Includes
 * the legacy ecommerce (lambda) mappings so approvals of legacy-held orders
 * enforce identically: the lambda's Controlled Substances percentage check
 * (controlPercentage) also writes CONTROLLED_SUBSTANCE_LIMIT, and its
 * license checks write the same license reasons.
 */
export const APPROVAL_REASON_CHECK_TYPES: Record<
	ApprovalRequiredReason,
	Array<OrderSubmissionCheckType>
> = {
	[ApprovalRequiredReason.CREDIT_HOLD]: ['creditHold'],
	[ApprovalRequiredReason.CREDIT_LIMIT]: ['creditLimit'],
	[ApprovalRequiredReason.CONTROLLED_SUBSTANCE_LIMIT]: [
		'dailyItemLimit',
		'monthlyItemLimit',
		'perOrderLimit',
		'controlPercentage',
	],
	[ApprovalRequiredReason.MAXIMUM_DISCOUNT]: ['maximumDiscount'],
	[ApprovalRequiredReason.MINIMUM_ORDER_PRICE]: ['minimumOrderPrice'],
	[ApprovalRequiredReason.OUT_OF_STOCK]: ['outOfStock'],
	[ApprovalRequiredReason.SHORT_DATE]: ['shortDate'],
	[ApprovalRequiredReason.PRICE_BELOW_MINIMUM]: ['minimumPrice'],
	[ApprovalRequiredReason.PRICE_BELOW_COST]: ['priceBelowCost'],
	[ApprovalRequiredReason.STATE_BOARD_LICENSE]: ['stateBoardLicense'],
	[ApprovalRequiredReason.DEA_LICENSE]: ['deaLicense'],
	// DEFAULT_SETTING isn't produced by a check row — its approver roles
	// live on the ordering.default_submission_status setting itself.
	[ApprovalRequiredReason.DEFAULT_SETTING]: [],
};

/**
 * Parse an order's `approvalRequiredReason` JSON column into reasons.
 * Unknown strings are preserved (they simply won't be coverable by the
 * mapping, so only blanket approvers can act on them). Returns [] for
 * null/empty/unparseable values.
 */
export function parseApprovalRequiredReasons(
	raw: string | null | undefined,
): Array<string> {
	if (raw == null || raw === '') {
		return [];
	}

	try {
		const parsed: unknown = JSON.parse(raw);

		if (!Array.isArray(parsed)) {
			return [];
		}

		return parsed.filter((r): r is string => typeof r === 'string');
	} catch {
		return [];
	}
}

/**
 * The role ids allowed (by configuration) to approve orders held for a
 * given reason on a given channel — the union of the approver roles on
 * every check row that can produce the reason, plus the default-status
 * setting's roles for DEFAULT_SETTING.
 */
export function getApproverRoleIdsForReason(
	reason: string,
	channel: OrderApprovalChannel,
	checkPreferences: Array<OrderSubmissionCheckPreference>,
	defaultStatusValue: DefaultSubmissionStatusValue | null | undefined,
): Array<number> {
	const roleIds = new Set<number>();

	if (reason === (ApprovalRequiredReason.DEFAULT_SETTING as string)) {
		const configured =
			channel === 'crm'
				? defaultStatusValue?.crmApproverRoleIds
				: defaultStatusValue?.ecommerceApproverRoleIds;

		for (const id of configured ?? []) {
			roleIds.add(id);
		}

		return [...roleIds];
	}

	// Indexed as a plain string map: `reason` comes off the order row and
	// may be a value this build doesn't know (newer producer) — those fall
	// through to [] so only blanket approvers can act on them.
	const checkTypes =
		(
			APPROVAL_REASON_CHECK_TYPES as Record<
				string,
				Array<OrderSubmissionCheckType> | undefined
			>
		)[reason] ?? [];

	for (const checkType of checkTypes) {
		const pref = checkPreferences.find((p) => p.type === checkType);
		const configured =
			channel === 'crm'
				? pref?.crmApproverRoleIds
				: pref?.ecommerceApproverRoleIds;

		for (const id of configured ?? []) {
			roleIds.add(id);
		}
	}

	return [...roleIds];
}

/**
 * True when the given roles cover EVERY reason on the order for the given
 * channel. An empty reason list returns false — an order held with no
 * recorded reason can only be actioned by blanket approvers.
 */
export function rolesCoverApprovalReasons(
	roleIds: Array<number>,
	reasons: Array<string>,
	channel: OrderApprovalChannel,
	checkPreferences: Array<OrderSubmissionCheckPreference>,
	defaultStatusValue: DefaultSubmissionStatusValue | null | undefined,
): boolean {
	if (reasons.length === 0 || roleIds.length === 0) {
		return false;
	}

	const held = new Set(roleIds);

	return reasons.every((reason) => {
		const allowed = getApproverRoleIdsForReason(
			reason,
			channel,
			checkPreferences,
			defaultStatusValue,
		);

		return allowed.some((id) => held.has(id));
	});
}
